  import { Component } from '@theme/component';
  import { trapFocus, removeTrapFocus } from '@theme/focus';
  import { onAnimationEnd } from '@theme/utilities';

  /**
   * A custom element that manages the main menu drawer.
   *
   * @typedef {object} Refs
   * @property {HTMLDetailsElement} details - The details element.
   * @property {HTMLElement} [tabsContainer] - The tabs container element.
   * @property {HTMLElement} [tabPanels] - The tab panels container element.
   *
   * @extends {Component<Refs>}
   */
  class HeaderDrawer extends Component {
    requiredRefs = ['details'];

    /** @type {number} */
    #activeTabIndex = 0;

    connectedCallback() {
      super.connectedCallback();

      this.addEventListener('keyup', this.#onKeyUp);
      this.#setupAnimatedElementListeners();
      this.#setupTabKeyboardNavigation();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.removeEventListener('keyup', this.#onKeyUp);
    }

    /**
     * Close the main menu drawer when the Escape key is pressed
     * @param {KeyboardEvent} event
     */
    #onKeyUp = (event) => {
      if (event.key !== 'Escape') return;

      this.#close(this.#getDetailsElement(event));
    };

    /**
     * @returns {boolean} Whether the main menu drawer is open
     */
    get isOpen() {
      return this.refs.details.hasAttribute('open');
    }

    /**
     * Get the closest details element to the event target
     * @param {Event | undefined} event
     * @returns {HTMLDetailsElement}
     */
    #getDetailsElement(event) {
      if (!(event?.target instanceof Element)) return this.refs.details;

      return event.target.closest('details') ?? this.refs.details;
    }

    /**
     * Toggle the main menu drawer
     */
    toggle() {
      return this.isOpen ? this.close() : this.open();
    }

    /**
     * Open the closest drawer or the main menu drawer
     * @param {Event} [event]
     */
    open(event) {
      const details = this.#getDetailsElement(event);
      const summary = details.querySelector('summary');

      if (!summary) return;

      summary.setAttribute('aria-expanded', 'true');

      this.preventInitialAccordionAnimations(details);
      requestAnimationFrame(() => {
        details.classList.add('menu-open');
        setTimeout(() => {
          trapFocus(details);
        }, 0);
      });
    }

    /**
     * Go back or close the main menu drawer
     * @param {Event} [event]
     */
    back(event) {
      this.#close(this.#getDetailsElement(event));
    }

    /**
     * Close the main menu drawer
     */
    close() {
      this.#close(this.refs.details);
    }

    /**
     * Close the closest menu or submenu that is open
     *
     * @param {HTMLDetailsElement} details
     */
    #close(details) {
      const summary = details.querySelector('summary');

      if (!summary) return;

      summary.setAttribute('aria-expanded', 'false');
      details.classList.remove('menu-open');

      onAnimationEnd(details, () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          setTimeout(() => {
            trapFocus(this.refs.details);
          }, 0);
        }
      });
    }

    /**
     * Attach animationend event listeners to all animated elements to remove will-change after animation
     * to remove the stacking context and allow submenus to be positioned correctly
     */
    #setupAnimatedElementListeners() {
      /**
       * @param {AnimationEvent} event
       */
      function removeWillChangeOnAnimationEnd(event) {
        const target = event.target;
        if (target && target instanceof HTMLElement) {
          target.style.setProperty('will-change', 'unset');
          target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
        }
      }
      const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
      allAnimated.forEach((element) => {
        element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
      });
    }

    /**
     * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
     * Adds a no-animation class to accordion content elements, then removes it after 100ms to
     * re-enable animations for user interactions.
     * @param {HTMLDetailsElement} details - The details element containing the accordions
     */
    preventInitialAccordionAnimations(details) {
      const content = details.querySelectorAll('accordion-custom .details-content');

      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.add('details-content--no-animation');
        }
      });
      setTimeout(() => {
        content.forEach((element) => {
          if (element instanceof HTMLElement) {
            element.classList.remove('details-content--no-animation');
          }
        });
      }, 100);
    }

    /**
     * Handle tab selection for the mobile menu
     * @param {Event} event - The click event from the tab button
     */
    selectTab(event) {
      const button = event.target instanceof Element ? event.target.closest('button[role="tab"]') : null;
      if (!(button instanceof HTMLElement)) return;

      const tabIndex = parseInt(button.dataset.tabIndex ?? '0', 10);
      this.#activateTab(tabIndex);
    }

    /**
     * Activate a specific tab by index
     * @param {number} index - The index of the tab to activate
     */
    #activateTab(index) {
      const tabsContainer = this.refs.tabsContainer;
      const tabPanels = this.refs.tabPanels;

      if (!tabsContainer || !tabPanels) return;

      const tabs = tabsContainer.querySelectorAll('button[role="tab"]');
      const panels = tabPanels.querySelectorAll('[role="tabpanel"]');

      if (index < 0 || index >= tabs.length) return;

      // Deactivate all tabs
      tabs.forEach((tab) => {
        tab.classList.remove('menu-drawer__tab--active');
        tab.setAttribute('aria-selected', 'false');
        tab.setAttribute('tabindex', '-1');
      });

      // Deactivate all panels
      panels.forEach((panel) => {
        panel.classList.remove('menu-drawer__tab-panel--active');
        panel.setAttribute('hidden', '');
      });

      // Activate the selected tab
      const selectedTab = tabs[index];
      const selectedPanel = panels[index];

      if (selectedTab && selectedPanel) {
        selectedTab.classList.add('menu-drawer__tab--active');
        selectedTab.setAttribute('aria-selected', 'true');
        selectedTab.setAttribute('tabindex', '0');

        if (selectedTab instanceof HTMLElement) {
          selectedTab.focus();
        }

        selectedPanel.classList.add('menu-drawer__tab-panel--active');
        selectedPanel.removeAttribute('hidden');

        // Scroll the active tab into view
        this.#scrollTabIntoView(selectedTab);
      }

      this.#activeTabIndex = index;
    }

    /**
     * Scroll the tab into view within the tabs container
     * @param {Element} tab - The tab element to scroll into view
     */
    #scrollTabIntoView(tab) {
      const tabsScroll = this.refs.tabsContainer?.querySelector('.menu-drawer__tabs-scroll');
      if (!tabsScroll || !(tab instanceof HTMLElement)) return;

      const tabRect = tab.getBoundingClientRect();
      const containerRect = tabsScroll.getBoundingClientRect();

      // Check if tab is out of view and scroll if needed
      if (tabRect.left < containerRect.left) {
        tabsScroll.scrollLeft -= containerRect.left - tabRect.left + 16;
      } else if (tabRect.right > containerRect.right) {
        tabsScroll.scrollLeft += tabRect.right - containerRect.right + 16;
      }
    }

    /**
     * Handle sub-tab selection (second-level navigation, e.g. Tops / Bottoms)
     * @param {Event} event - The click event from the sub-tab button
     */
    selectSubTab(event) {
      const button = event.target instanceof Element ? event.target.closest('button[role="tab"]') : null;
      if (!(button instanceof HTMLElement)) return;

      const subTabList = button.closest('[role="tablist"]');
      const tabPanel = button.closest('[role="tabpanel"]');
      if (!subTabList || !tabPanel) return;

      const subTabIndex = parseInt(button.dataset.subTabIndex ?? '0', 10);

      const allSubTabs = subTabList.querySelectorAll('button[role="tab"]');
      const allSubPanels = tabPanel.querySelectorAll('[data-sub-panel]');

      allSubTabs.forEach((tab) => {
        tab.classList.remove('menu-drawer__sub-tab--active');
        tab.setAttribute('aria-selected', 'false');
      });

      allSubPanels.forEach((panel) => {
        panel.classList.remove('menu-drawer__sub-panel--active');
        panel.setAttribute('hidden', '');
      });

      const selectedTab = allSubTabs[subTabIndex];
      const selectedPanel = allSubPanels[subTabIndex];

      if (selectedTab) {
        selectedTab.classList.add('menu-drawer__sub-tab--active');
        selectedTab.setAttribute('aria-selected', 'true');
      }

      if (selectedPanel) {
        selectedPanel.classList.add('menu-drawer__sub-panel--active');
        selectedPanel.removeAttribute('hidden');
      }
    }

    /**
     * Setup keyboard navigation for tabs (arrow keys)
     */
    #setupTabKeyboardNavigation() {
      const tabsContainer = this.refs.tabsContainer;
      if (!tabsContainer) return;

      tabsContainer.addEventListener('keydown', (event) => {
        if (!(event.target instanceof Element)) return;

        const tabs = tabsContainer.querySelectorAll('button[role="tab"]');
        const tabCount = tabs.length;

        if (tabCount === 0) return;

        let newIndex = this.#activeTabIndex;

        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            newIndex = (this.#activeTabIndex - 1 + tabCount) % tabCount;
            break;
          case 'ArrowRight':
            event.preventDefault();
            newIndex = (this.#activeTabIndex + 1) % tabCount;
            break;
          case 'Home':
            event.preventDefault();
            newIndex = 0;
            break;
          case 'End':
            event.preventDefault();
            newIndex = tabCount - 1;
            break;
          default:
            return;
        }

        this.#activateTab(newIndex);
      });
    }
  }

  if (!customElements.get('header-drawer')) {
    customElements.define('header-drawer', HeaderDrawer);
  }

  /**
   * Reset an open details element to its original state
   *
   * @param {HTMLDetailsElement} element
   */
  function reset(element) {
    element.classList.remove('menu-open');
    element.removeAttribute('open');
    element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  }
